import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  MinusOutlined,
} from "@ant-design/icons";

import { toPersianNumber } from "../../utils/helpers";

/**
 * A bare count answers "how many" but never "is that good", so the card takes an
 * optional delta against the previous period. `invertDelta` is for metrics where
 * going up is bad (overdue work), which would otherwise be painted green.
 */
const DeltaBadge = ({ delta, label, invert }) => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;

  const flat = Math.round(delta) === 0;
  const positive = delta > 0;
  const good = invert ? !positive : positive;
  const tone = flat ? "flat" : good ? "up" : "down";

  const Icon = flat ? MinusOutlined : positive ? ArrowUpOutlined : ArrowDownOutlined;

  // The comparison text lives in a tooltip rather than on the card: spelled out
  // in full it wrapped onto a second line, which made cards carrying a delta
  // taller than the ones next to them.
  return (
    <Tooltip title={label}>
      <span className={`kpi-delta kpi-delta-${tone}`}>
        <Icon className="kpi-delta-icon" aria-hidden />
        <span>{toPersianNumber(Math.abs(Math.round(delta)))}٪</span>
      </span>
    </Tooltip>
  );
};

/** A flat inline sparkline; enough to read a direction without a chart library. */
const Sparkline = ({ points = [], tone = "blue" }) => {
  if (!Array.isArray(points) || points.length < 2) return null;

  const max = Math.max(...points);
  const min = Math.min(...points);

  // A series with no variation (typically all zeros) draws a dead straight rule
  // that reads as a stray underline rather than as data.
  if (max === min) return null;

  const span = max - min;
  const step = 100 / (points.length - 1);

  const path = points
    .map((value, index) => {
      const x = index * step;
      const y = 28 - ((value - min) / span) * 24;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      className={`kpi-sparkline kpi-sparkline-${tone}`}
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      aria-hidden
      focusable="false"
    >
      <path d={path} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const KpiCard = ({
  icon,
  tone = "blue",
  iconClass,
  label,
  value,
  href,
  hint,
  suffix,
  delta,
  deltaLabel,
  invertDelta = false,
  sparkline,
}) => {
  const inner = (
    <div className="kpi-card">
      <div className={`kpi-icon ${iconClass || `kpi-icon-${tone}`}`}>{icon}</div>
      <div className="kpi-body">
        <div className="kpi-label">
          <span className="kpi-label-text">{label}</span>
          {hint && (
            <Tooltip title={hint} trigger={["hover", "click"]}>
              <InfoCircleOutlined className="kpi-hint" />
            </Tooltip>
          )}
        </div>
        <div className="kpi-value">
          {value ?? 0}
          {suffix && <span className="kpi-suffix">{suffix}</span>}
        </div>
        <DeltaBadge delta={delta} label={deltaLabel} invert={invertDelta} />
      </div>
      <Sparkline points={sparkline} tone={tone} />
    </div>
  );

  return href ? (
    <Link to={href} className="kpi-card-link">
      {inner}
    </Link>
  ) : (
    inner
  );
};

export default KpiCard;
